import {HTMLAttributes, useEffect, useRef, useState} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {getGqlErrorMessage} from '@/api/request';
import {useVarifySmsCode} from '@/api/session';
import {VerifySmsCodeData} from '@/interface';
import {palette} from '@/styles';
import {Button} from '../Button';
import {TextField} from '../TextField';
import {Typography, typographyStyle} from '../Typography';
import {InlineFieldWrapper, InputContainer, TextFieldLabel} from './StyledComponents';

interface Inputs {
  code: string;
  cellPhone: string;
  smsVerificationId: string;
}

interface IProps extends HTMLAttributes<HTMLFormElement> {
  cellPhone?: string;
  smsData?: VerifySmsCodeData;
  onComplete(): void;
}

const getTimeFormat = (time: number) => {
  const min = Math.floor(time / 60);
  const sec = time % 60;

  return `${min}:${sec > 9 ? sec : `0${sec}`}`;
};

export const PhoneVerifyCode = ({
  smsData,
  cellPhone,
  onComplete,
  ...props
}: IProps) => {
  const {
    control,
    setFocus,
    handleSubmit,
    register,
    setError,
    formState: {errors, isValid},
  } = useForm<Inputs>({mode: 'all'});

  const timer = useRef<NodeJS.Timer>();
  const [remainTime, setRemianTime] = useState<number>(180);

  const {mutate: verifySms, isPending} = useVarifySmsCode();

  const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
    verifySms(data, {
      onSuccess: (res) => {
        if (res?.result?.success) {
          onComplete();
        } else {
          const message =
            res.result?.message === '인증에 실패하였습니다.'
              ? '인증 번호가 일치하지 않습니다.'
              : res.result?.message;

          setError('code', {
            type: 'server-error',
            message:
              message ||
              '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
          });
        }
      },
      onError: (serverError) => {
        if (serverError) {
          setError('code', {
            type: 'server-error',
            message: getGqlErrorMessage(
              serverError,
              '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
            ),
          });
        }
      },
    });
  };

  const clearTimer = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = undefined;
    }
  };

  useEffect(() => {
    setFocus('code');
  }, [setFocus]);

  useEffect(() => {
    if (smsData?.smsVerificationId) {
      setRemianTime(180);

      timer.current = setInterval(() => {
        setRemianTime((prev) => prev - 1);
      }, 1000);

      return () => clearTimer();
    }
  }, [smsData?.smsVerificationId]);

  useEffect(() => {
    if (remainTime <= 0) {
      clearTimer();

      setError('code', {
        type: 'timeout',
        message: '인증 시간이 만료되었습니다. 인증 번호를 재전송 해 주세요.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainTime]);

  if (!smsData) {
    return null;
  }

  const error = errors['code']?.message;

  return (
    <Form as="form" {...props} onSubmit={handleSubmit(onSubmit)}>
      <input
        hidden
        {...register('smsVerificationId')}
        value={smsData.smsVerificationId}
      />
      <input hidden {...register('cellPhone')} value={cellPhone} />
      <TextFieldLabel error={!!error?.length}>인증 번호</TextFieldLabel>
      <div>
        <InlineFieldWrapper>
          <Controller
            control={control}
            name="code"
            rules={{
              required: '인증 번호를 입력해 주세요.',
              pattern: /^\d+$/,
              maxLength: 4,
              minLength: {
                value: 4,
                message: '인증 번호는 4자리입니다.',
              },
            }}
            render={({field}) => (
              <FieldWrapper>
                <TextField
                  ref={field.ref}
                  maxLength={4}
                  fullWidth
                  variant="xlarge"
                  placeholder="인증 번호 4자리"
                  value={field.value}
                  pattern="[0-9]*"
                  onChange={(event) => {
                    field.onChange(event.target.value.replace(/\D/g, ''));
                  }}
                  error={!!error?.length}
                />

                <Timer
                  style={{
                    color: remainTime ? palette.primary400 : palette.red400,
                  }}
                >
                  {getTimeFormat(remainTime)}
                </Timer>
              </FieldWrapper>
            )}
          />
          <Button
            type="submit"
            disabled={!remainTime || !isValid || isPending}
            variant="fill"
            color="secondary"
            size="mediumLarge"
            fullWidth
          >
            {isPending ? '요청 중...' : '인증하기'}
          </Button>
        </InlineFieldWrapper>
        {!!error?.length && <ErrorText>{error}</ErrorText>}
      </div>
      <Typography
        variant="paragraph12"
        lineHeight="20px"
        color={palette.gray600}
        fontWeight={400}
      >
        인증 번호가 오지 않으면 입력하신 정보가 정확한지 확인해 주세요.
        <br />
        입력 시간 초과 시 '재전송' 버튼을 눌러주세요.
      </Typography>
    </Form>
  );
};

const Form = styled(InputContainer)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Timer = styled.div`
  ${{...typographyStyle.medium14}}
  position: absolute;
  right: 16px;
  top: 15px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const ErrorText = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  margin-top: 8px;
  color: ${palette.red400};
`;
